import { Analytics } from '@gik/analytics';
import { AnalyticsEvents } from '@gik/analytics/utils/Events';
import type { FallbackProps } from '@gik/core/api/BaseAPIConfig';
import { withTitle } from '@gik/core/hocs/withTitle';
import routes from '@gik/core/routes';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import type { IHomePageProps } from '@gik/landing-pages/pages/HomePage';
import type { ILandingPageProps } from '@gik/landing-pages/pages/LandingPage';
import { LandingPage } from '@gik/landing-pages/pages/LandingPage';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import { useCallback } from 'react';
import { SWRConfig } from 'swr';

export default function LandingPageTemplateComp({ fallback }: FallbackProps<IHomePageProps>) {
  const handleCtaClick = useCallback(() => {
    Analytics.fireEvent(AnalyticsEvents.GroupOnboardingClicked, { location: 'GroupLandingPage' });
  }, []);

  return withTitle(
    'For Groups',
    <SWRConfig value={{ fallback }}>
      <LandingPage
        {...landingPageProps}
        url="/organize-care-group"
        ctaLabel="Start a Group"
        ctaHref={routes.groupOnboarding}
        onCtaClick={handleCtaClick}
      />
    </SWRConfig>
  );
}

LandingPageTemplateComp.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

// FIXME: Replace this strategy with i18n library instead
// @ts-ignore
const landingPageProps: ILandingPageProps = {
  heroSection: {
    hero: {
      title: 'For Groups',
      content: `Whether you need to organize support for one person or many, Give InKind is here to help. And it's always free.`,
    },
    backgroundImage: `${getAssetsUrl()}landing-pages/headers/hero-office-mobile@2x.png`,
    backgroundImageMd: `${getAssetsUrl()}landing-pages/headers/hero-office-desktop@2x.png`,
    backgroundColor: '#eff6f8',
  },
  featureFocus: {
    feature: {
      title: 'Why Choose Give InKind?',
      icon: `${getAssetsUrl()}landing-pages/groups.png`,
      features: [
        <>
          <span className={'tw-font-bold'}>Flexible Support, Exactly How You Need It:</span> Coordinate meals, gifts,
          fundraising, or volunteer efforts effortlessly, adapting to your group&apos;s unique needs.
        </>,
        <>
          <span className={'tw-font-bold'}>Receive Help from Anywhere:</span> Easily involve supporters both near and
          far, ensuring everyone can participate regardless of location.
        </>,
        <>
          <span className={'tw-font-bold'}>Stay Connected with Reminders and Updates:</span> Automated reminders and
          updates keep your group informed and engaged, simplifying communication.
        </>,
        <>
          <span className={'tw-font-bold'}>Advanced Features Designed for Groups:</span> Enjoy tailored features
          including a branded page to showcase your group&apos;s identity and inspire community involvement.
        </>,
        <>
          <span className={'tw-font-bold'}>Dedicated Support Ready to Assist You:</span> Receive personalized assistance
          from our expert support team, ensuring your experience is smooth and successful from start to finish.
        </>,
      ],
    },
  },
  ctaSection: {
    ctas: [
      {
        title: 'For Companies & Teams',
        content:
          'Whether your team is in the same room or spread across the globe, we can help you ensure everyone feels supported when it counts.',
        icon: `${getAssetsUrl()}landing-pages/features/Groups-Feature01.png`,
        buttonTitle: 'Read More',
        buttonLink: '/articles/powering-team-support-give-inkind-groups',
      },
      {
        title: 'For Churches & Faith Groups',
        content:
          'Churches and companies of all kinds use Give InKind to centralize the ongoing needs of members and employees. Bring individuals together and strengthen group culture.',
        icon: `${getAssetsUrl()}landing-pages/features/Groups-Feature02.png`,
      },
      {
        title: 'For Schools & Classrooms',
        content:
          'Easily create a classroom signup page to organize supplies, volunteers, and teacher appreciation. Keep families connected and support flowing all year long.',
        icon: `${getAssetsUrl()}landing-pages/features/Groups-Feature03.png`,
        // form: {
        //   portalId: '5851908',
        //   formId: 'fead4046-8b4d-4589-8611-0ec3a081a877',
        //   region: 'na1',
        // },
        buttonTitle: 'Read More',
        buttonLink: '/articles/create-classroom-sign-page-give-inkind',
      },
    ],
  },
};
